import { contentMaxWidths, readingContent } from "@/src/theme/layout";
import { Alert } from "@/src/lib/productAlert";
import { InsetModal as Modal } from "@/src/components/ui/InsetModal";
import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import { Ionicons } from "@/src/components/ui/ionicons";
import { router, useLocalSearchParams } from "expo-router";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { PostCard } from "../../src/components/PostCard";
import { GroupModerationQueue } from "../../src/components/GroupModerationQueue";
import { useBlockedUsers } from "../../src/hooks/useBlockedUsers";
import { useActiveAccount } from "../../src/hooks/useActiveAccount";
import { useMyPostLikes } from "../../src/hooks/useMyPostLikes";
import {
  ensureIdentityGroupConversation,
  setIdentityGroupPostingMode,
  subscribeIdentityGroupConversation,
} from "../../src/lib/identityGroups";
import { auth, db } from "../../src/lib/firebase";
import {
  approveJoinRequest,
  cancelPrivateGroupRequest,
  loadGroupMembers,
  leaveGroupMembership,
  listenGroupMembers,
  loadJoinRequests,
  removeGroupMember,
  rejectJoinRequest,
  requestPrivateGroupJoin,
  syncPublicGroupJoin,
  toggleGroupVisibility,
  updateGroupMemberRole,
  updateGroupDetails,
  archiveGroup,
  type GroupMemberItem,
  type JoinRequestItem,
} from "../../src/lib/groupMemberships";
import { blockUser } from "../../src/lib/blocks";
import { friendlyFirebaseError } from "../../src/lib/friendlyFirebaseError";
import { reportPost } from "../../src/lib/reports";
import { togglePostLike } from "../../src/lib/postLikes";
import { SUBJECTS, SUBJECT_LABEL } from "../../src/lib/taxonomy";
import type { FirePost } from "../../src/types/models";
import { getAccountType, getPrimaryIdentityBadge } from "../../src/lib/identity";
import type { AccountType, LegacyAccountType } from "../../src/types/identity";

const BG = surface.secondary;
const ACCENT = brand.primary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;

type Group = {
  id: string;
  name: string;
  description?: string;
  subject?: string;
  isPublic?: boolean;
  ownerIdentityId?: string;
  /** @deprecated Compatibility snapshot for historical groups. */
  creatorRole?: LegacyAccountType;
  creatorAccountType?: AccountType;
  membersCount?: number;
  status?: "active" | "archived" | string;
  createdAt?: any;
};

function friendlyFirestoreError(err: any): string {
  const msg = String(err?.message ?? err ?? "");
  if (msg.toLowerCase().includes("requires an index")) {
    return "Index Firestore manquant. Cree l'index propose dans Firebase Console.";
  }
  return friendlyFirebaseError(err, "Impossible de charger le groupe. Réessaie.");
}

function mapGroup(data: any, id: string): Group {
  return {
    id,
    name: data?.name ?? "Groupe",
    description: data?.description ?? "",
    subject: data?.subject ?? "",
    isPublic: !!data?.isPublic,
    ownerIdentityId: data?.ownerIdentityId ?? "",
    creatorAccountType: getAccountType({
      accountType: data?.creatorAccountType,
      role: data?.creatorRole,
    }) ?? undefined,
    ...(data?.creatorRole === "student" || data?.creatorRole === "teacher"
      ? { creatorRole: data.creatorRole }
      : {}),
    membersCount: typeof data?.membersCount === "number" ? data.membersCount : 0,
    status: data?.status ?? "active",
    createdAt: data?.createdAt,
  };
}

type GroupHeaderProps = {
  group: Group | null;
  gid: string;
  user: typeof auth.currentUser;
  isMember: boolean;
  isOwner: boolean;
  canDeleteGroup: boolean;
  isGroupManager: boolean;
  requestSent: boolean;
  joining: boolean;
  leaving: boolean;
  updatingVisibility: boolean;
  settingsExpanded: boolean;
  setSettingsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  editingName: string;
  setEditingName: React.Dispatch<React.SetStateAction<string>>;
  editingDescription: string;
  setEditingDescription: React.Dispatch<React.SetStateAction<string>>;
  editingSubject: string;
  setEditingSubject: React.Dispatch<React.SetStateAction<string>>;
  savingSettings: boolean;
  deletingGroup: boolean;
  membersExpanded: boolean;
  setMembersExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  loadingMembers: boolean;
  membersCountLabel: string;
  membersContent: React.ReactNode;
  loadingJoinRequests: boolean;
  joinRequestsContent: React.ReactNode;
  groupChatExists: boolean;
  groupChatBusy: boolean;
  groupChatCanOpen: boolean;
  groupChatPostingMode: "all_members" | "admins_only";
  error: string | null;
  onBack: () => void;
  onOpenCreatePost: () => void;
  onJoinPublicGroup: () => void;
  onRequestPrivateGroup: () => void;
  onCancelRequest: () => void;
  onLeaveGroup: () => void;
  onToggleVisibility: () => void;
  onSaveSettings: () => void;
  onDeleteGroup: () => void;
  onLaunchGroupChat: () => void;
  onOpenGroupChat: () => void;
  onToggleGroupChatPostingMode: () => void;
  groupModerationContent?: React.ReactNode;
  moderationExpanded: boolean;
  setModerationExpanded: React.Dispatch<React.SetStateAction<boolean>>;
};

const GroupHeader = React.memo(function GroupHeader(props: GroupHeaderProps) {
  const isArchived = props.group?.status === "archived";
  const [menuOpen, setMenuOpen] = useState(false);
  const showPublish = !!props.user && props.isMember && !isArchived;
  const showJoin = !!props.user && !props.isMember && !!props.group?.isPublic && !isArchived;
  const showRequest =
    !!props.user &&
    !props.isMember &&
    props.group?.isPublic === false &&
    !props.requestSent &&
    !isArchived;
  const showCancelRequest =
    !!props.user && !props.isMember && props.group?.isPublic === false && props.requestSent;
  const showLeave = !!props.user && props.isMember && !props.isOwner;

  const openPanel = (panel: "settings" | "members" | "moderation") => {
    props.setSettingsExpanded(panel === "settings");
    props.setMembersExpanded(panel === "members");
    props.setModerationExpanded(panel === "moderation");
    setMenuOpen(false);
  };

  const runMenuAction = (action: () => void) => {
    setMenuOpen(false);
    action();
  };

  const menuItem = (
    label: string,
    icon: React.ComponentProps<typeof Ionicons>["name"],
    onPress: () => void,
    destructive = false
  ) => (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={{
        minHeight: 50,
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 16,
      }}
    >
      <Ionicons name={icon} size={21} color={destructive ? uiText.danger : uiText.primary} />
      <Text style={{ color: destructive ? uiText.danger : uiText.primary, fontWeight: "800", fontSize: 15 }}>
        {label}
      </Text>
    </Pressable>
  );

  return (
    <View style={{ paddingHorizontal: 16, paddingTop: 8, gap: 10 }}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <Pressable
          onPress={props.onBack}
          accessibilityRole="button"
          accessibilityLabel="Retour"
          hitSlop={8}
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            backgroundColor: CARD,
            borderWidth: 1,
            borderColor: BORDER,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Ionicons name="chevron-back" size={20} color={uiText.primary} />
        </Pressable>

        <View style={{ flex: 1 }}>
          <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>
            {props.group?.name ?? "Groupe"}
          </Text>
          <Text style={{ color: MUTED, marginTop: 2 }}>
            {isArchived ? "Archive" : props.group?.isPublic ? "Public" : "Prive"}
            {props.membersCountLabel ? ` · ${props.membersCountLabel}` : ""}
            {props.group?.subject
              ? ` · ${SUBJECT_LABEL[props.group.subject as keyof typeof SUBJECT_LABEL] ?? props.group.subject}`
              : ""}
          </Text>
        </View>

        {props.isMember || props.isGroupManager || props.canDeleteGroup ? (
          <Pressable
            onPress={() => setMenuOpen(true)}
            accessibilityRole="button"
            accessibilityLabel="Actions du groupe"
            hitSlop={6}
            style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
          >
            <Ionicons name="ellipsis-horizontal" size={22} color={uiText.primary} />
          </Pressable>
        ) : null}
      </View>

      {showPublish ? (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          <Pressable
            onPress={props.onOpenCreatePost}
            accessibilityRole="button"
            accessibilityLabel="Publier dans le groupe"
            style={{
              flex: Platform.OS === "web" ? undefined : 1,
              paddingHorizontal: 16,
              minHeight: 44,
              borderRadius: 12,
              backgroundColor: ACCENT,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              gap: 8,
            }}
          >
            <Ionicons name="create-outline" size={19} color={uiText.primary} />
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>Publier</Text>
          </Pressable>

          {props.isGroupManager || props.groupChatCanOpen ? (
            <Pressable
              onPress={props.groupChatExists ? props.onOpenGroupChat : props.onLaunchGroupChat}
              disabled={props.groupChatBusy || (props.groupChatExists && !props.groupChatCanOpen)}
              accessibilityRole="button"
              accessibilityLabel={props.groupChatExists ? "Ouvrir la discussion" : "Créer la discussion"}
              style={{
                flex: Platform.OS === "web" ? undefined : 1,
              paddingHorizontal: 16,
                minHeight: 44,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: BORDER,
                backgroundColor: CARD,
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "row",
                gap: 8,
                opacity: props.groupChatBusy ? 0.65 : 1,
              }}
            >
              <Ionicons name="chatbubble-outline" size={19} color={uiText.primary} />
              <Text style={{ color: uiText.primary, fontWeight: "900" }}>Discussion</Text>
            </Pressable>
          ) : null}
        </View>
      ) : showJoin ? (
          <Pressable
            onPress={props.onJoinPublicGroup}
            disabled={props.joining}
            accessibilityRole="button"
            accessibilityLabel="Rejoindre le groupe"
            style={{
              minHeight: 44,
              paddingHorizontal: 12,
              borderRadius: 12,
              backgroundColor: ACCENT,
              alignItems: "center",
              justifyContent: "center",
              opacity: props.joining ? 0.7 : 1,
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>
              {props.joining ? "..." : "Rejoindre"}
            </Text>
          </Pressable>
      ) : showRequest ? (
          <Pressable
            onPress={props.onRequestPrivateGroup}
            disabled={props.joining}
            accessibilityRole="button"
            accessibilityLabel="Demander à rejoindre le groupe"
            style={{
              height: 40,
              paddingHorizontal: 12,
              borderRadius: 12,
              backgroundColor: ACCENT,
              alignItems: "center",
              justifyContent: "center",
              opacity: props.joining ? 0.7 : 1,
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>
              {props.joining ? "..." : "Demander"}
            </Text>
          </Pressable>
      ) : showCancelRequest ? (
          <Pressable
            onPress={props.onCancelRequest}
            disabled={props.joining}
            accessibilityRole="button"
            accessibilityLabel="Annuler la demande d'accès"
            style={{
              height: 40,
              paddingHorizontal: 12,
              borderRadius: 12,
              backgroundColor: surface.elevated,
              alignItems: "center",
              justifyContent: "center",
              opacity: props.joining ? 0.7 : 1,
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>
              {props.joining ? "..." : "Annuler"}
            </Text>
          </Pressable>
      ) : null}

      {props.group?.description ? (
        <View
          style={{
            backgroundColor: CARD,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 14,
            paddingHorizontal: 14,
            paddingVertical: 12,
            gap: 7,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 7 }}>
            <Ionicons name="information-circle-outline" size={18} color={uiText.brand} />
            <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 13 }}>À propos du groupe</Text>
          </View>
          <Text style={{ color: uiText.secondary, lineHeight: 21 }}>{props.group.description}</Text>
        </View>
      ) : null}

      {props.settingsExpanded && (props.isOwner || props.canDeleteGroup) && !isArchived ? (
        <View
          style={{
            backgroundColor: CARD,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 16,
            padding: 12,
            gap: 10,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>Modifier le groupe</Text>
            <Pressable
              onPress={() => props.setSettingsExpanded(false)}
              accessibilityRole="button"
              accessibilityLabel="Fermer la modification du groupe"
              style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
            >
              <Ionicons name="close" size={22} color={uiText.primary} />
            </Pressable>
          </View>

            <View style={{ gap: 10 }}>
              {props.isOwner ? (
                <>
                  <BrandTextInput
                    value={props.editingName}
                    onChangeText={props.setEditingName}
                    placeholder="Nom du groupe"
                    placeholderTextColor={uiText.muted}
                    style={{
                      color: uiText.primary,
                      backgroundColor: surface.primary,
                      borderWidth: 1,
                      borderColor: BORDER,
                      borderRadius: 12,
                      paddingHorizontal: 12,
                      paddingVertical: 12,
                    }}
                  />

                  <BrandTextInput
                    value={props.editingDescription}
                    onChangeText={props.setEditingDescription}
                    placeholder="Description"
                    placeholderTextColor={uiText.muted}
                    multiline
                    style={{
                      color: uiText.primary,
                      backgroundColor: surface.primary,
                      borderWidth: 1,
                      borderColor: BORDER,
                      borderRadius: 12,
                      paddingHorizontal: 12,
                      paddingVertical: 12,
                      minHeight: 90,
                      textAlignVertical: "top",
                    }}
                  />

                  <View style={{ gap: 8 }}>
                    <Text style={{ color: uiText.primary, fontWeight: "800" }}>Matiere</Text>
                    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                      {SUBJECTS.map((subject) => {
                        const active = props.editingSubject === subject.value;
                        return (
                          <Pressable
                            key={subject.value}
                            onPress={() => props.setEditingSubject(subject.value)}
                            style={{
                              paddingHorizontal: 10,
                              paddingVertical: 8,
                              borderRadius: 999,
                              borderWidth: 1,
                              borderColor: active ? ACCENT : BORDER,
                              backgroundColor: active
                                ? surface.selected
                                : surface.primary,
                            }}
                          >
                            <Text style={{ color: uiText.primary, fontWeight: "800", fontSize: 12 }}>
                              {subject.label}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>

                  <View style={{ flexDirection: "row", gap: 10 }}>
                    <Pressable
                      onPress={props.onSaveSettings}
                      disabled={props.savingSettings}
                      style={{
                        flex: 1,
                        borderRadius: 12,
                        backgroundColor: ACCENT,
                        alignItems: "center",
                        justifyContent: "center",
                        paddingVertical: 12,
                        opacity: props.savingSettings ? 0.7 : 1,
                      }}
                    >
                      <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                        {props.savingSettings ? "Enregistrement..." : "Enregistrer"}
                      </Text>
                    </Pressable>

                  </View>
                </>
              ) : (
                <Pressable
                  onPress={props.onDeleteGroup}
                  disabled={props.deletingGroup}
                  style={{
                    borderRadius: 12,
                    backgroundColor: surface.danger,
                    borderWidth: 1,
                    borderColor: border.danger,
                    alignItems: "center",
                    justifyContent: "center",
                    paddingVertical: 12,
                    opacity: props.deletingGroup ? 0.7 : 1,
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                    {props.deletingGroup ? "Archivage..." : "Archiver le groupe"}
                  </Text>
                </Pressable>
              )}
            </View>
        </View>
      ) : null}

      {!props.isMember && props.group?.isPublic === false ? (
        <View
          style={{
            backgroundColor: CARD,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 16,
            padding: 12,
          }}
        >
          <Text style={{ color: uiText.primary, fontWeight: "900" }}>Groupe prive</Text>
          <Text style={{ color: MUTED, marginTop: 6 }}>
            Rejoins ce groupe pour publier et voir son contenu prive.
          </Text>
        </View>
      ) : null}

      {props.isMember && props.membersExpanded ? (
        <View
          style={{
            backgroundColor: CARD,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 16,
            padding: 12,
            gap: 10,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>Membres · {props.membersCountLabel}</Text>
            <Pressable
              onPress={() => props.setMembersExpanded(false)}
              accessibilityRole="button"
              accessibilityLabel="Fermer la liste des membres"
              style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
            >
              <Ionicons name="close" size={22} color={uiText.primary} />
            </Pressable>
          </View>
          {props.membersContent}
          {props.isGroupManager && props.group?.isPublic === false && !isArchived ? (
            <View style={{ gap: 10 }}>
              <Text style={{ color: uiText.primary, fontWeight: "900" }}>Demandes d&apos;accès</Text>
              {props.loadingJoinRequests ? <ActivityIndicator color={uiText.brand} /> : props.joinRequestsContent}
            </View>
          ) : null}
        </View>
      ) : null}

      {props.error ? (
        <View
          style={{
            backgroundColor: surface.danger,
            borderWidth: 1,
            borderColor: border.danger,
            borderRadius: 16,
            padding: 12,
          }}
        >
          <Text style={{ color: uiText.danger, fontWeight: "800" }}>Erreur</Text>
          <Text style={{ color: uiText.danger, marginTop: 6 }}>{props.error}</Text>
        </View>
      ) : null}

      {props.moderationExpanded ? (
        <View style={{ gap: 8 }}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>Modération</Text>
            <Pressable
              onPress={() => props.setModerationExpanded(false)}
              accessibilityRole="button"
              accessibilityLabel="Fermer la modération"
              style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
            >
              <Ionicons name="close" size={22} color={uiText.primary} />
            </Pressable>
          </View>
          {props.groupModerationContent}
        </View>
      ) : null}

      <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 16 }}>Posts</Text>

      <Modal visible={menuOpen} transparent animationType="fade" onRequestClose={() => setMenuOpen(false)}>
        <Pressable
          onPress={() => setMenuOpen(false)}
          style={{ flex: 1, backgroundColor: surface.overlay, justifyContent: Platform.OS === "web" ? "center" : "flex-end", alignItems: "center", padding: Platform.OS === "web" ? 16 : 0 }}
        >
          <Pressable
            onPress={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: Platform.OS === "web" ? contentMaxWidths.dialog : undefined,
              backgroundColor: surface.primary,
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              paddingTop: 8,
              paddingBottom: 24,
            }}
          >
            <View style={{ alignItems: "center", paddingVertical: 8 }}>
              <View style={{ width: 42, height: 4, borderRadius: 999, backgroundColor: surface.elevated }} />
            </View>
            <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 17, paddingHorizontal: 16, paddingVertical: 10 }}>
              Actions du groupe
            </Text>
            {props.isOwner ? menuItem("Modifier le groupe", "create-outline", () => openPanel("settings")) : null}
            {props.isOwner
              ? menuItem(
                  props.updatingVisibility
                    ? "Mise à jour..."
                    : props.group?.isPublic
                    ? "Passer en privé"
                    : "Passer en public",
                  props.group?.isPublic ? "lock-closed-outline" : "globe-outline",
                  () => runMenuAction(props.onToggleVisibility)
                )
              : null}
            {props.isMember
              ? menuItem(`Membres · ${props.membersCountLabel}`, "people-outline", () => openPanel("members"))
              : null}
            {props.isGroupManager && props.groupChatExists
              ? menuItem(
                  props.groupChatPostingMode === "admins_only"
                    ? "Messages : tous les membres"
                    : "Messages : admins seulement",
                  "chatbubbles-outline",
                  () => runMenuAction(props.onToggleGroupChatPostingMode)
                )
              : null}
            {props.isGroupManager
              ? menuItem("Modération", "shield-checkmark-outline", () => openPanel("moderation"))
              : null}
            {showLeave
              ? menuItem(props.leaving ? "Départ..." : "Quitter le groupe", "exit-outline", () => runMenuAction(props.onLeaveGroup), true)
              : null}
            {props.canDeleteGroup
              ? menuItem(props.deletingGroup ? "Archivage..." : "Archiver le groupe", "archive-outline", () => runMenuAction(props.onDeleteGroup), true)
              : null}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
});

export default function GroupScreen() {
  const { groupId } = useLocalSearchParams<{ groupId: string }>();
  const user = auth.currentUser;
  const { activeAccountId } = useActiveAccount();
  const blockedUserIds = useBlockedUsers(user?.uid);
  const gid = useMemo(() => String(groupId || ""), [groupId]);

  const [group, setGroup] = useState<Group | null>(null);
  const [loadingGroup, setLoadingGroup] = useState(true);
  const [loadingMembership, setLoadingMembership] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);

  const [posts, setPosts] = useState<FirePost[]>([]);
  const { likedPostIds, setLikedPostIds } = useMyPostLikes(
    posts.map((post) => post.id),
    user?.uid
  );
  const [error, setError] = useState<string | null>(null);
  const [isMember, setIsMember] = useState(false);
  const [memberRole, setMemberRole] = useState<"owner" | "member" | "admin" | null>(null);
  const [requestSent, setRequestSent] = useState(false);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const [joinRequests, setJoinRequests] = useState<JoinRequestItem[]>([]);
  const [loadingJoinRequests, setLoadingJoinRequests] = useState(false);
  const [processingRequestUid, setProcessingRequestUid] = useState<string | null>(null);
  const [members, setMembers] = useState<GroupMemberItem[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [processingMemberUid, setProcessingMemberUid] = useState<string | null>(null);
  const [processingMemberAction, setProcessingMemberAction] = useState<
    "remove" | "promote" | "demote" | null
  >(null);
  const [membersExpanded, setMembersExpanded] = useState(false);
  const [moderationExpanded, setModerationExpanded] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [editingDescription, setEditingDescription] = useState("");
  const [editingSubject, setEditingSubject] = useState("general");
  const [savingSettings, setSavingSettings] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [groupChatExists, setGroupChatExists] = useState(false);
  const [groupChatBusy, setGroupChatBusy] = useState(false);
  const [groupChatPostingMode, setGroupChatPostingMode] = useState<"all_members" | "admins_only">(
    "all_members"
  );

  const [likesOverlay, setLikesOverlay] = useState<
    Record<string, { likedBy: string[]; likesCount: number }>
  >({});
  const isOwner = !!activeAccountId && group?.ownerIdentityId === activeAccountId;
  const isArchived = group?.status === "archived";
  const effectiveIsMember = isMember || isOwner;
  const isGroupManager = isOwner || memberRole === "admin" || memberRole === "owner";
  const canDeleteGroup = isOwner && !isArchived;

  useEffect(() => {
    if (!gid || !activeAccountId || !effectiveIsMember) {
      setGroupChatExists(false);
      setGroupChatPostingMode("all_members");
      return;
    }

    return subscribeIdentityGroupConversation(
      gid,
      (conversation) => {
        setGroupChatExists(!!conversation);
        setGroupChatPostingMode(
          conversation?.whoCanPost === "admins_only" ? "admins_only" : "all_members"
        );
      },
      () => {
        setGroupChatExists(false);
        setGroupChatPostingMode("all_members");
      }
    );
  }, [activeAccountId, effectiveIsMember, gid]);

  useEffect(() => {
    if (!group) return;

    setEditingName(group.name ?? "");
    setEditingDescription(group.description ?? "");
    setEditingSubject(group.subject ?? "general");
  }, [group]);

  useEffect(() => {
    if (!gid) return;

    setLoadingGroup(true);
    setError(null);

    const unsub = onSnapshot(
      doc(db, "groups", gid),
      (snap) => {
        if (!snap.exists()) {
          setGroup(null);
          setError("Groupe introuvable.");
          setLoadingGroup(false);
          return;
        }

        setGroup(mapGroup(snap.data(), snap.id));
        setLoadingGroup(false);
      },
      (err) => {
        setGroup(null);
        setError(friendlyFirestoreError(err));
        setLoadingGroup(false);
      }
    );

    return () => unsub();
  }, [gid]);

  useEffect(() => {
    if (!gid || !activeAccountId) {
      setIsMember(false);
      setMemberRole(null);
      setRequestSent(false);
      setLoadingMembership(false);
      return;
    }

    setLoadingMembership(true);

    let memberResolved = false;
    let requestResolved = false;

    function finishIfReady() {
      if (memberResolved && requestResolved) {
        setLoadingMembership(false);
      }
    }

    const unsubMember = onSnapshot(
      doc(db, "groups", gid, "members", activeAccountId),
      (snap) => {
        setIsMember(snap.exists());
        setMemberRole(
          !snap.exists()
            ? null
            : (snap.data() as any)?.role === "owner"
            ? "owner"
            : (snap.data() as any)?.role === "admin"
            ? "admin"
            : "member"
        );
        memberResolved = true;
        finishIfReady();
      },
      () => {
        setIsMember(false);
        setMemberRole(null);
        memberResolved = true;
        finishIfReady();
      }
    );

    const unsubRequest = onSnapshot(
      doc(db, "groups", gid, "joinRequests", activeAccountId),
      (snap) => {
        setRequestSent(snap.exists());
        requestResolved = true;
        finishIfReady();
      },
      () => {
        setRequestSent(false);
        requestResolved = true;
        finishIfReady();
      }
    );

    return () => {
      unsubMember();
      unsubRequest();
    };
  }, [activeAccountId, gid]);

  useEffect(() => {
    if (!gid || !group || loadingMembership) return;

    if (!group.isPublic && !effectiveIsMember) {
      setPosts([]);
      setLoadingPosts(false);
      return;
    }

    setLoadingPosts(true);

    const postsQuery =
      group.isPublic && !effectiveIsMember
        ? query(
            collection(db, "posts"),
            where("visibility", "==", "group"),
            where("groupId", "==", gid),
            where("moderationStatus", "==", "approved"),
            orderBy("createdAt", "desc"),
            limit(50)
          )
        : query(
            collection(db, "posts"),
            where("visibility", "==", "group"),
            where("groupId", "==", gid),
            where("moderationStatus", "==", "approved"),
            orderBy("createdAt", "desc"),
            limit(50)
          );

    const unsub = onSnapshot(
      postsQuery,
      (snap) => {
        const items = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() as any),
        })) as FirePost[];

        setPosts(items.filter((item) =>
          (!item.moderationStatus || item.moderationStatus === "approved")
          && !blockedUserIds.has(item.authorUid)
        ));
        setLoadingPosts(false);
      },
      (err) => {
        setPosts([]);
        setLoadingPosts(false);
        setError(friendlyFirestoreError(err));
      }
    );

    return () => unsub();
  }, [blockedUserIds, gid, group, effectiveIsMember, loadingMembership]);

  useEffect(() => {
    if (!gid || !isGroupManager) {
      setJoinRequests([]);
      setLoadingJoinRequests(false);
      return;
    }

    setLoadingJoinRequests(true);

    const unsub = onSnapshot(
      collection(db, "groups", gid, "joinRequests"),
      async () => {
        try {
          const items = await loadJoinRequests(gid);
          setJoinRequests(items);
        } catch {
          setJoinRequests([]);
        } finally {
          setLoadingJoinRequests(false);
        }
      },
      () => {
        setJoinRequests([]);
        setLoadingJoinRequests(false);
      }
    );

    return () => unsub();
  }, [gid, isGroupManager]);

  useEffect(() => {
    if (!gid || !effectiveIsMember) {
      setMembers([]);
      setLoadingMembers(false);
      return;
    }

    setLoadingMembers(true);

    const unsub = listenGroupMembers(
      gid,
      (items) => {
        setMembers(items);
        setLoadingMembers(false);
      },
      () => {
        setMembers([]);
        setLoadingMembers(false);
      }
    );

    return () => unsub();
  }, [gid, effectiveIsMember]);

  async function joinPublicGroup() {
    if (!activeAccountId || !group) return;
    if (group.status === "archived") {
      Alert.alert("Groupe archive", "Ce groupe n'accepte plus de nouveaux membres.");
      return;
    }

    setJoining(true);
    try {
      await syncPublicGroupJoin({
        identityId: activeAccountId,
        group: {
          id: gid,
          name: group.name,
          isPublic: !!group.isPublic,
          ownerIdentityId: group.ownerIdentityId,
        },
      });

      setIsMember(true);
      setRequestSent(false);
    } catch (e: any) {
      Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible de rejoindre le groupe."));
    } finally {
      setJoining(false);
    }
  }

  async function requestPrivateGroup() {
    if (!activeAccountId || !group) return;
    if (group.status === "archived") {
      Alert.alert("Groupe archive", "Ce groupe n'accepte plus de demandes.");
      return;
    }

    setJoining(true);
    try {
      await requestPrivateGroupJoin({
        identityId: activeAccountId,
        groupId: gid,
      });
      setRequestSent(true);
    } catch (e: any) {
      Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible d'envoyer la demande."));
    } finally {
      setJoining(false);
    }
  }

  async function cancelRequest() {
    if (!activeAccountId) return;

    setJoining(true);
    try {
      await cancelPrivateGroupRequest({
        identityId: activeAccountId,
        groupId: gid,
      });
      setRequestSent(false);
    } catch (e: any) {
      Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible d'annuler la demande."));
    } finally {
      setJoining(false);
    }
  }

  async function leaveGroup() {
    if (!activeAccountId || !group) return;

    setLeaving(true);
    try {
      await leaveGroupMembership({
        identityId: activeAccountId,
        group: {
          id: gid,
          name: group.name,
          isPublic: !!group.isPublic,
          ownerIdentityId: group.ownerIdentityId,
        },
      });

      setIsMember(false);
    } catch (e: any) {
      Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible de quitter le groupe."));
    } finally {
      setLeaving(false);
    }
  }

  async function handleApproveRequest(targetIdentityId: string) {
    if (!activeAccountId || !group) return;

    setProcessingRequestUid(targetIdentityId);
    try {
      await approveJoinRequest({
        actorIdentityId: activeAccountId,
        group: {
          id: gid,
          name: group.name,
          isPublic: !!group.isPublic,
          ownerIdentityId: group.ownerIdentityId,
        },
        targetIdentityId,
      });

      setJoinRequests((prev) => prev.filter((item) => item.identityId !== targetIdentityId));
    } catch (e: any) {
      Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible d'accepter la demande."));
    } finally {
      setProcessingRequestUid(null);
    }
  }

  async function handleRejectRequest(targetIdentityId: string) {
    setProcessingRequestUid(targetIdentityId);
    try {
      await rejectJoinRequest({
        actorIdentityId: activeAccountId!,
        groupId: gid,
        targetIdentityId,
      });

      setJoinRequests((prev) => prev.filter((item) => item.identityId !== targetIdentityId));
    } catch (e: any) {
      Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible de refuser la demande."));
    } finally {
      setProcessingRequestUid(null);
    }
  }

  async function handleRemoveMember(targetIdentityId: string) {
    if (!group || !activeAccountId) return;

    setProcessingMemberUid(targetIdentityId);
    setProcessingMemberAction("remove");
    try {
      await removeGroupMember({
        group: {
          id: gid,
          name: group.name,
          isPublic: !!group.isPublic,
          ownerIdentityId: group.ownerIdentityId,
        },
        targetIdentityId,
        actorIdentityId: activeAccountId!,
      });
    } catch (e: any) {
      Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible de retirer ce membre."));
    } finally {
      setProcessingMemberUid(null);
      setProcessingMemberAction(null);
    }
  }

  async function handleChangeMemberRole(targetIdentityId: string, role: "admin" | "member") {
    if (!group || !activeAccountId) return;

    setProcessingMemberUid(targetIdentityId);
    setProcessingMemberAction(role === "admin" ? "promote" : "demote");
    try {
      await updateGroupMemberRole({
        group: {
          id: gid,
          name: group.name,
          isPublic: !!group.isPublic,
          ownerIdentityId: group.ownerIdentityId,
        },
        actorIdentityId: activeAccountId!,
        targetIdentityId,
        role,
      });
    } catch (e: any) {
      Alert.alert(
        "Erreur",
        friendlyFirebaseError(
          e,
          role === "admin"
            ? "Impossible de promouvoir ce membre."
            : "Impossible de retrograder cet admin."
        )
      );
    } finally {
      setProcessingMemberUid(null);
      setProcessingMemberAction(null);
    }
  }

  async function handleToggleVisibility() {
    if (!group || !isOwner) {
      Alert.alert("Acces refuse", "Seul le proprietaire peut changer la visibilite du groupe.");
      return;
    }

    const nextIsPublic = !group.isPublic;
    const title = nextIsPublic ? "Passer en public" : "Passer en prive";
    const message = nextIsPublic
      ? "Le groupe deviendra visible et accessible sans demande. Les membres actuels seront conserves."
      : "Le groupe deviendra prive. Les membres actuels seront conserves, mais les nouveaux utilisateurs devront envoyer une demande.";

    Alert.alert(title, message, [
      { text: "Annuler", style: "cancel" },
      {
        text: nextIsPublic ? "Rendre public" : "Rendre prive",
        onPress: async () => {
          try {
            setUpdatingVisibility(true);
            await toggleGroupVisibility({
              group: {
                id: gid,
                name: group.name,
                isPublic: !!group.isPublic,
                ownerIdentityId: group.ownerIdentityId,
              },
              isPublic: nextIsPublic,
              actorIdentityId: activeAccountId!,
            });
          } catch (e: any) {
            Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible de changer la visibilite du groupe."));
          } finally {
            setUpdatingVisibility(false);
          }
        },
      },
    ]);
  }

  async function handleSaveSettings() {
    if (!group || !isOwner) {
      Alert.alert("Acces refuse", "Seul le proprietaire peut modifier ce groupe.");
      return;
    }

    const cleanName = editingName.trim();
    const cleanDescription = editingDescription.trim();

    if (cleanName.length < 3) {
      Alert.alert("Erreur", "Le nom du groupe doit contenir au moins 3 caracteres.");
      return;
    }

    if (cleanName.length > 60) {
      Alert.alert("Erreur", "Le nom du groupe est trop long.");
      return;
    }

    if (cleanDescription.length > 220) {
      Alert.alert("Erreur", "La description est trop longue.");
      return;
    }

    try {
      setSavingSettings(true);
      await updateGroupDetails({
        group: {
          id: gid,
          name: group.name,
          isPublic: !!group.isPublic,
          ownerIdentityId: group.ownerIdentityId,
          description: group.description,
          subject: group.subject,
        },
        name: cleanName,
        description: cleanDescription,
        subject: editingSubject,
        actorIdentityId: activeAccountId!,
      });
    } catch (e: any) {
      Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible de mettre a jour le groupe."));
    } finally {
      setSavingSettings(false);
    }
  }

  async function handleDeleteGroup() {
    if (!group || !canDeleteGroup) {
      Alert.alert("Acces refuse", "Seul le proprietaire peut archiver ce groupe.");
      return;
    }

    Alert.alert(
      "Archiver le groupe",
      "Le groupe ne pourra plus recevoir de nouveaux membres ni de nouveaux posts. Les contenus existants restent conserves.",
      [
        { text: "Annuler", style: "cancel" },
        {
          text: deletingGroup ? "..." : "Archiver",
          style: "destructive",
          onPress: async () => {
            try {
              setDeletingGroup(true);
              await archiveGroup({
                id: gid,
                name: group.name,
                isPublic: !!group.isPublic,
                ownerIdentityId: group.ownerIdentityId,
                description: group.description,
                subject: group.subject,
                actorIdentityId: activeAccountId!,
              });
              router.replace("/(tabs)/profile");
            } catch (e: any) {
              Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible d'archiver le groupe."));
            } finally {
              setDeletingGroup(false);
            }
          },
        },
      ]
    );
  }

  async function handleLaunchGroupChat() {
    if (!activeAccountId || !group || groupChatBusy) return;

    try {
      setGroupChatBusy(true);
      await ensureIdentityGroupConversation(activeAccountId, gid);
      router.push({
        pathname: "/(tabs)/messages/group-thread/[id]",
        params: { id: gid },
      });
    } catch (e: any) {
      Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible de lancer la discussion du groupe."));
    } finally {
      setGroupChatBusy(false);
    }
  }

  function handleOpenGroupChat() {
    if (!groupChatExists || !effectiveIsMember) return;

    router.push({
      pathname: "/(tabs)/messages/group-thread/[id]",
      params: { id: gid },
    });
  }

  function handleToggleGroupChatPostingMode() {
    if (!group || !groupChatExists || !isGroupManager) return;

    const nextMode = groupChatPostingMode === "admins_only" ? "all_members" : "admins_only";
    const title =
      nextMode === "admins_only" ? "Restreindre le chat aux admins" : "Autoriser tous les membres";
    const message =
      nextMode === "admins_only"
        ? "Seuls les admins pourront envoyer des messages dans ce chat."
        : "Tous les membres du groupe pourront envoyer des messages dans ce chat.";

    Alert.alert(title, message, [
      { text: "Annuler", style: "cancel" },
      {
        text: "Confirmer",
        onPress: async () => {
          try {
            setGroupChatBusy(true);
            if (!activeAccountId) return;
            await setIdentityGroupPostingMode(activeAccountId, gid, nextMode);
          } catch (e: any) {
            Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible de mettre a jour les permissions."));
          } finally {
            setGroupChatBusy(false);
          }
        },
      },
    ]);
  }

  async function toggleLike(postId: string) {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const current = posts.find((post) => post.id === postId);
    const overlayCurrent = likesOverlay[postId];
    const baseLikedBy = Array.isArray(overlayCurrent?.likedBy)
      ? overlayCurrent.likedBy
      : Array.isArray(current?.likedBy)
      ? current.likedBy
      : [];
    const baseLikes = Number.isInteger(overlayCurrent?.likesCount)
      ? overlayCurrent.likesCount
      : Number.isInteger(current?.likesCount)
      ? (current?.likesCount as number)
      : baseLikedBy.length;

    const alreadyLiked = likedPostIds.has(postId) || baseLikedBy.includes(currentUser.uid);
    const nextLikedBy = alreadyLiked
      ? baseLikedBy.filter((uid) => uid !== currentUser.uid)
      : [...baseLikedBy, currentUser.uid];
    const nextLikes = alreadyLiked ? Math.max(0, baseLikes - 1) : baseLikes + 1;
    const previousOverlay = likesOverlay[postId];

    setLikesOverlay((prev) => ({
      ...prev,
      [postId]: { likedBy: nextLikedBy, likesCount: nextLikes },
    }));
    setLikedPostIds((prev) => {
      const next = new Set(prev);
      if (alreadyLiked) next.delete(postId);
      else next.add(postId);
      return next;
    });

    try {
      await togglePostLike({ postId, uid: currentUser.uid, currentlyLiked: alreadyLiked });
    } catch {
      setLikedPostIds((prev) => {
        const next = new Set(prev);
        if (alreadyLiked) next.add(postId);
        else next.delete(postId);
        return next;
      });
      setLikesOverlay((prev) => {
        const next = { ...prev };
        if (previousOverlay) {
          next[postId] = previousOverlay;
        } else {
          delete next[postId];
        }
        return next;
      });
    }
  }

  function openPostModerationActions(item: FirePost) {
    if (!user?.uid || item.authorUid === user.uid) return;

    Alert.alert("Post", "Choisis une action.", [
      { text: "Annuler", style: "cancel" },
      {
        text: "Signaler",
        onPress: async () => {
          try {
            await reportPost({
              reporterUid: user.uid,
              postId: item.id,
              reason: "other",
              details: "Signalement depuis un groupe",
            });
            Alert.alert("Signalement envoye", "Merci, ce post sera examine.");
          } catch (e: any) {
            Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible de signaler ce post."));
          }
        },
      },
      {
        text: "Bloquer l'auteur",
        style: "destructive",
        onPress: async () => {
          try {
            await blockUser({ uid: user.uid, blockedUid: item.authorUid });
            Alert.alert("Utilisateur bloque", "Ses contenus seront masques pour toi.");
          } catch (e: any) {
            Alert.alert("Erreur", friendlyFirebaseError(e, "Impossible de bloquer cet utilisateur."));
          }
        },
      },
    ]);
  }

  function renderRequestCard(item: JoinRequestItem) {
    const label = item.username ? `@${item.username}` : item.displayName || item.identityId;
    const identityLabel = getPrimaryIdentityBadge({
      accountType: item.accountType,
      role: item.role,
      verificationStatus: item.verificationStatus,
      entitlements: item.entitlements,
    })?.label;
    const subtitle = [identityLabel, item.track || item.mainSubject].filter(Boolean).join(" · ");
    const busy = processingRequestUid === item.identityId;

    return (
      <Pressable
        key={item.identityId}
        onPress={() => router.push(`/profile/${item.identityId}`)}
        style={{
          borderRadius: 14,
          borderWidth: 1,
          borderColor: BORDER,
          backgroundColor: surface.primary,
          padding: 12,
          gap: 10,
        }}
      >
        <View>
          <Text style={{ color: uiText.primary, fontWeight: "800" }}>{label}</Text>
          {!!subtitle ? <Text style={{ color: MUTED, marginTop: 4 }}>{subtitle}</Text> : null}
        </View>

        <View style={{ flexDirection: "row", gap: 10 }}>
          <Pressable
            onPress={() => handleApproveRequest(item.identityId)}
            disabled={busy}
            style={{
              flex: 1,
              borderRadius: 12,
              backgroundColor: ACCENT,
              alignItems: "center",
              justifyContent: "center",
              paddingVertical: 10,
              opacity: busy ? 0.7 : 1,
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>
              {busy ? "..." : "Accepter"}
            </Text>
          </Pressable>

          <Pressable
            onPress={() => handleRejectRequest(item.identityId)}
            disabled={busy}
            style={{
              flex: 1,
              borderRadius: 12,
              backgroundColor: surface.danger,
              borderWidth: 1,
              borderColor: border.danger,
              alignItems: "center",
              justifyContent: "center",
              paddingVertical: 10,
              opacity: busy ? 0.7 : 1,
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>Refuser</Text>
          </Pressable>
        </View>
      </Pressable>
    );
  }

  function renderMemberCard(item: GroupMemberItem) {
    const label = item.username ? `@${item.username}` : item.displayName || item.identityId;
    const identityLabel = getPrimaryIdentityBadge({
      accountType: item.profileAccountType,
      role: item.profileRole,
      verificationStatus: item.profileVerificationStatus,
      entitlements: item.profileEntitlements,
    })?.label;
    const subtitle = [identityLabel, item.track || item.mainSubject]
      .filter(Boolean)
      .join(" · ");
    const isTargetOwner = !!group?.ownerIdentityId && item.identityId === group.ownerIdentityId;
    const isMe = item.identityId === activeAccountId;
    const canPromote = isOwner && !isTargetOwner && item.role !== "admin";
    const canDemote = isOwner && !isTargetOwner && item.role === "admin";
    const canRemove = isOwner
      ? !isTargetOwner && !isMe && item.role !== "admin"
      : isGroupManager && !isTargetOwner && !isMe && item.role !== "admin";
    const busy = processingMemberUid === item.identityId;
    const roleLabel = isTargetOwner || item.role === "owner" ? "Owner" : item.role === "admin" ? "Admin" : null;
    const busyLabel =
      processingMemberAction === "promote"
        ? "Promotion..."
        : processingMemberAction === "demote"
        ? "Mise a jour..."
        : "...";

    return (
      <Pressable
        key={item.identityId}
        onPress={() => router.push(`/profile/${item.identityId}`)}
        style={{
          borderRadius: 14,
          borderWidth: 1,
          borderColor: BORDER,
          backgroundColor: surface.primary,
          padding: 12,
          gap: 10,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <View style={{ flex: 1 }}>
            <Text style={{ color: uiText.primary, fontWeight: "800" }}>{label}</Text>
            <Text style={{ color: MUTED, marginTop: 4 }}>
              {subtitle || " "}
            </Text>
          </View>

          {roleLabel ? (
            <View
              style={{
                paddingHorizontal: 8,
                paddingVertical: 5,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: isTargetOwner
                  ? surface.selected
                  : surface.info,
                backgroundColor: isTargetOwner
                  ? surface.selected
                  : surface.info,
              }}
            >
              <Text style={{ color: uiText.primary, fontSize: 11, fontWeight: "900" }}>{roleLabel}</Text>
            </View>
          ) : null}
        </View>

        {canPromote || canDemote || canRemove ? (
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {canPromote ? (
              <Pressable
                onPress={(event) => {
                  event.stopPropagation();
                  handleChangeMemberRole(item.identityId, "admin");
                }}
                disabled={busy}
                style={{
                  borderRadius: 12,
                  backgroundColor: surface.info,
                  borderWidth: 1,
                  borderColor: brand.secondary,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  opacity: busy ? 0.7 : 1,
                }}
              >
                <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                  {busy ? busyLabel : "Promouvoir admin"}
                </Text>
              </Pressable>
            ) : null}

            {canDemote ? (
              <Pressable
                onPress={(event) => {
                  event.stopPropagation();
                  handleChangeMemberRole(item.identityId, "member");
                }}
                disabled={busy}
                style={{
                  borderRadius: 12,
                  backgroundColor: surface.elevated,
                  borderWidth: 1,
                  borderColor: BORDER,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  opacity: busy ? 0.7 : 1,
                }}
              >
                <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                  {busy ? busyLabel : "Retirer admin"}
                </Text>
              </Pressable>
            ) : null}

            {canRemove ? (
              <Pressable
                onPress={(event) => {
                  event.stopPropagation();
                  Alert.alert(
                    "Retirer ce membre",
                    `Tu veux retirer ${label} du groupe ?`,
                    [
                      { text: "Annuler", style: "cancel" },
                      {
                        text: busy ? "..." : "Retirer",
                        style: "destructive",
                        onPress: () => handleRemoveMember(item.identityId),
                      },
                    ]
                  );
                }}
                disabled={busy}
                style={{
                  borderRadius: 12,
                  backgroundColor: surface.danger,
                  borderWidth: 1,
                  borderColor: border.danger,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  opacity: busy ? 0.7 : 1,
                }}
              >
                <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                  {busy && processingMemberAction === "remove" ? "..." : "Retirer"}
                </Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}
      </Pressable>
    );
  }

  function renderPost({ item }: { item: FirePost }) {
    const overlay = likesOverlay[item.id];
    const merged = overlay
      ? { ...item, likedBy: overlay.likedBy, likesCount: overlay.likesCount }
      : item;

    const likedBy = Array.isArray(merged.likedBy) ? merged.likedBy : [];
    const liked = !!user?.uid && (likedPostIds.has(item.id) || likedBy.includes(user.uid));
    const likes: number =
      typeof merged.likesCount === "number" ? merged.likesCount : likedBy.length;

    return (
      <View style={{ marginHorizontal: 16, marginBottom: 12 }}>
        <PostCard
          item={{
            ...merged,
            subjectLabel: merged.subject
              ? SUBJECT_LABEL[merged.subject as keyof typeof SUBJECT_LABEL] ?? merged.subject
              : "General",
          }}
          liked={liked}
          likes={likes}
          isMine={!!user?.uid && merged.authorUid === user.uid}
          onToggleLike={toggleLike}
          onOpenActions={openPostModerationActions}
          onSharePost={(post) =>
            router.push({
              pathname: "/share/post/[postId]",
              params: { postId: post.id },
            })
          }
          disableGroupLink
        />
      </View>
    );
  }

  const visibleAdminMembers = members.filter(
    (item) => item.identityId === group?.ownerIdentityId || item.role === "admin"
  );

  const membersContent = loadingMembers ? (
    <ActivityIndicator color={uiText.brand} />
  ) : members.length === 0 ? (
    <Text style={{ color: MUTED }}>
      {effectiveIsMember ? "Aucun membre charge pour le moment." : "Rejoins ce groupe pour voir les membres."}
    </Text>
  ) : isGroupManager ? (
    members.map(renderMemberCard)
  ) : visibleAdminMembers.length === 0 ? (
    <Text style={{ color: MUTED }}>Aucun admin visible pour le moment.</Text>
  ) : (
    <View style={{ gap: 10 }}>
      {visibleAdminMembers.map(renderMemberCard)}
    </View>
  );

  const joinRequestsContent = joinRequests.length === 0 ? (
    <Text style={{ color: MUTED }}>Aucune demande en attente pour le moment.</Text>
  ) : (
    joinRequests.map(renderRequestCard)
  );

  if (!gid) {
    return (
      <SafeAreaView
        style={{ flex: 1, backgroundColor: BG, alignItems: "center", justifyContent: "center" }}
      >
        <Text style={{ color: uiText.primary }}>groupId manquant.</Text>
      </SafeAreaView>
    );
  }

  if (loadingGroup || loadingMembership) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: BG }} edges={["top", "bottom", "left", "right"]}>
        <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
          <ActivityIndicator color={uiText.brand} />
          <Text style={{ color: MUTED, marginTop: 10 }}>Chargement du groupe...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!group) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: BG }} edges={["top", "bottom", "left", "right"]}>
        <View style={{ padding: 16, gap: 16 }}>
          <Pressable
            onPress={() => router.back()}
            accessibilityRole="button"
            accessibilityLabel="Retour"
            hitSlop={8}
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              backgroundColor: CARD,
              borderWidth: 1,
              borderColor: BORDER,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Ionicons name="chevron-back" size={20} color={uiText.primary} />
          </Pressable>

          <View
            style={{
              borderRadius: 18,
              padding: 16,
              backgroundColor: CARD,
              borderWidth: 1,
              borderColor: BORDER,
            }}
          >
            <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>
              {error ?? "Groupe introuvable."}
            </Text>
            <Text style={{ color: MUTED, marginTop: 8, lineHeight: 20 }}>
              Ce groupe n’est pas disponible ou tu n’as pas accès à son contenu.
            </Text>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }} edges={["top", "bottom", "left", "right"]}>
      <FlatList
        data={posts}
        keyExtractor={(item) => item.id}
        renderItem={renderPost}
        ListHeaderComponent={
          <GroupHeader
            group={group}
            gid={gid}
              user={user}
              isMember={effectiveIsMember}
              isOwner={isOwner}
              canDeleteGroup={canDeleteGroup}
              isGroupManager={isGroupManager}
            requestSent={requestSent}
            joining={joining}
            leaving={leaving}
            updatingVisibility={updatingVisibility}
            settingsExpanded={settingsExpanded}
            setSettingsExpanded={setSettingsExpanded}
            editingName={editingName}
            setEditingName={setEditingName}
            editingDescription={editingDescription}
            setEditingDescription={setEditingDescription}
            editingSubject={editingSubject}
            setEditingSubject={setEditingSubject}
            savingSettings={savingSettings}
            deletingGroup={deletingGroup}
            membersExpanded={membersExpanded}
            setMembersExpanded={setMembersExpanded}
            moderationExpanded={moderationExpanded}
            setModerationExpanded={setModerationExpanded}
            loadingMembers={loadingMembers}
            membersCountLabel={
              loadingMembers
                ? "Chargement..."
                : effectiveIsMember
                ? `${members.length} membre${members.length > 1 ? "s" : ""}`
                : typeof group?.membersCount === "number"
                ? `~${group.membersCount} membre${group.membersCount > 1 ? "s" : ""}`
                : ""
            }
            membersContent={membersContent}
            loadingJoinRequests={loadingJoinRequests}
            joinRequestsContent={joinRequestsContent}
            groupChatExists={groupChatExists}
            groupChatBusy={groupChatBusy}
            groupChatCanOpen={groupChatExists && effectiveIsMember}
            groupChatPostingMode={groupChatPostingMode}
            error={error}
            onBack={() => router.back()}
            onOpenCreatePost={() =>
              router.push({
                pathname: "/(tabs)/create",
                params: { audience: "group", groupId: gid },
              })
            }
            onJoinPublicGroup={joinPublicGroup}
            onRequestPrivateGroup={requestPrivateGroup}
            onCancelRequest={cancelRequest}
            onLeaveGroup={() =>
              Alert.alert("Quitter le groupe", "Tu veux vraiment quitter ce groupe ?", [
                { text: "Annuler", style: "cancel" },
                {
                  text: leaving ? "..." : "Quitter",
                  style: "destructive",
                  onPress: leaveGroup,
                },
              ])
            }
            onToggleVisibility={handleToggleVisibility}
            onSaveSettings={handleSaveSettings}
            onDeleteGroup={handleDeleteGroup}
            onLaunchGroupChat={handleLaunchGroupChat}
            onOpenGroupChat={handleOpenGroupChat}
            onToggleGroupChatPostingMode={handleToggleGroupChatPostingMode}
            groupModerationContent={isGroupManager ? <GroupModerationQueue groupId={gid} /> : null}
          />
        }
        contentContainerStyle={{ ...readingContent(contentMaxWidths.group), paddingBottom: 24 }}
        showsVerticalScrollIndicator={Platform.OS === "web"}
        initialNumToRender={6}
        maxToRenderPerBatch={6}
        windowSize={7}
        removeClippedSubviews={Platform.OS === "android"}
        ListEmptyComponent={
          loadingPosts ? (
            <View style={{ padding: 16 }}>
              <ActivityIndicator color={uiText.brand} />
              <Text style={{ color: MUTED, marginTop: 10 }}>Chargement du feed...</Text>
            </View>
          ) : (
            <View
              style={{
                marginHorizontal: 16,
                marginTop: 12,
                backgroundColor: CARD,
                borderWidth: 1,
                borderColor: BORDER,
                borderRadius: 18,
                padding: 14,
              }}
            >
              <Text style={{ color: uiText.primary, fontWeight: "900" }}>Aucun post</Text>
              <Text style={{ color: MUTED, marginTop: 6 }}>
                {isArchived
                  ? "Ce groupe est archive. Les contenus existants restent consultables selon tes droits."
                  : group?.isPublic || effectiveIsMember
                  ? "Sois le premier a publier dans ce groupe."
                  : "Rejoins ce groupe pour voir et publier du contenu."}
              </Text>
            </View>
          )
        }
      />
    </SafeAreaView>
  );
}


